import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import {
  FeatherAlertCircle,
  FeatherAtSign,
  FeatherInbox,
  FeatherMail,
  FeatherPaperclip,
  FeatherRefreshCw,
  FeatherSettings,
} from "@subframe/core";
import {
  MailAccountDTO,
  MailMessageDTO,
  MailMessageSummaryDTO,
  SyncResultDTO,
  connectMailAccount,
  disconnectMailAccount,
  getMail,
  listMail,
  listMailAccounts,
  syncMail,
  updateMailAccount,
} from "../../data/api/botMailApi";
import { MailSettingsCard } from "../components/MailSettingsCard";
import { MessageViewer } from "../components/MessageViewer";

/**
 * "Zoho Books Automation Accounts → Mail box".
 *
 * Reading is always from the backend's cache; "Fetch from Gmail" is the only
 * action that touches Google. The window defaults to today from the mailbox's
 * fetch_after_time (21:00 IST out of the box) up to now — the "mails after 9pm
 * today" view — and both ends are editable.
 */

/**
 * ISO strings from the API carry the mailbox's own offset
 * ("2026-07-29T21:00:00+05:30"), so the first 16 characters are already the
 * mailbox-local value a datetime-local input wants. Slicing beats re-deriving
 * it through the browser's timezone.
 */
const toLocalInput = (iso: string): string => iso.slice(0, 16);

const formatWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const statusBadge = (account: MailAccountDTO) => {
  if (account.status === "connected") return <Badge variant="success">Connected</Badge>;
  if (account.status === "error") return <Badge variant="error">Needs attention</Badge>;
  return <Badge variant="warning">Disconnected</Badge>;
};

export const BotMailboxPage: React.FC = () => {
  const [accounts, setAccounts] = useState<MailAccountDTO[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [oauthConfigured, setOauthConfigured] = useState(true);

  const [messages, setMessages] = useState<MailMessageSummaryDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [windowFrom, setWindowFrom] = useState("");
  const [windowTo, setWindowTo] = useState("");
  const [windowTz, setWindowTz] = useState("");

  const [selected, setSelected] = useState<MailMessageDTO | null>(null);
  const [messageLoading, setMessageLoading] = useState(false);

  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<SyncResultDTO | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const [connectEmail, setConnectEmail] = useState("prasanhakumar@notionpress.com");
  const [connectLabel, setConnectLabel] = useState("Zoho Books inbox");
  const [showConnect, setShowConnect] = useState(false);

  const activeAccount = useMemo(
    () => accounts.find((a) => a.public_id === activeId) ?? null,
    [accounts, activeId]
  );

  // ── Connect outcome, handed back by the Google callback ────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("bot_mail_connect");
    if (!outcome) return;

    if (outcome === "success") {
      const replaced = params.get("replaced");
      setNotice(
        `Connected ${params.get("email") || "the mailbox"}.` +
          (replaced ? ` Replaced ${replaced} — its fetched mail is still stored.` : "") +
          " Fetch to pull mail in."
      );
    } else {
      setError(params.get("reason") || "Google connect failed.");
    }

    // Strip the params so a refresh doesn't re-announce a stale result.
    params.delete("bot_mail_connect");
    params.delete("email");
    params.delete("mailbox");
    params.delete("replaced");
    params.delete("reason");
    const query = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}`
    );
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      const res = await listMailAccounts();
      setAccounts(res.data.accounts);
      setOauthConfigured(res.data.oauth_configured);
      setActiveId((prev) => {
        if (prev && res.data.accounts.some((a) => a.public_id === prev)) return prev;
        return res.data.default_public_id ?? res.data.accounts[0]?.public_id ?? null;
      });
    } catch (err: any) {
      setError(err.message || "Failed to load mailboxes");
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  /**
   * Load the cached window. Passing no window on the first call lets the server
   * apply the mailbox's own default, which then populates the two inputs.
   */
  const fetchList = useCallback(
    async (publicId: string, from?: string, to?: string) => {
      try {
        setLoadingList(true);
        setError(null);
        const res = await listMail({
          public_id: publicId,
          from: from || null,
          to: to || null,
          limit: 200,
        });
        setMessages(res.data.messages);
        setTotal(res.data.total);
        setWindowFrom(toLocalInput(res.data.window.start));
        setWindowTo(toLocalInput(res.data.window.end));
        setWindowTz(res.data.window.timezone);
      } catch (err: any) {
        setError(err.message || "Failed to load mail");
        setMessages([]);
        setTotal(0);
      } finally {
        setLoadingList(false);
      }
    },
    []
  );

  // Switching mailbox resets the window back to that mailbox's own default.
  useEffect(() => {
    if (!activeId) return;
    setSelected(null);
    fetchList(activeId);
  }, [activeId, fetchList]);

  const handleSync = async () => {
    if (!activeId) return;
    try {
      setSyncing(true);
      setError(null);
      setNotice(null);
      const res = await syncMail({
        public_id: activeId,
        from: windowFrom || null,
        to: windowTo || null,
      });
      setLastSync(res.data.sync);
      setAccounts((prev) =>
        prev.map((a) => (a.public_id === activeId ? res.data.account : a))
      );
      await fetchList(activeId, windowFrom, windowTo);

      const { fetched, new: fresh, skipped, failed, truncated } = res.data.sync;
      setNotice(
        `Gmail returned ${fetched} mail(s) in this window — ${fresh} new` +
          (skipped ? `, ${skipped} outside the window` : "") +
          (failed ? `, ${failed} failed` : "") +
          (truncated ? ". Hit the per-sync cap — narrow the window for the rest." : ".")
      );
    } catch (err: any) {
      setError(err.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleOpen = async (row: MailMessageSummaryDTO) => {
    try {
      setMessageLoading(true);
      setError(null);
      // Show what's already in the list immediately; swap in the full row next.
      setSelected({
        ...row,
        to_emails: null,
        cc_emails: null,
        // delivered_to already comes through on the list row.
        reply_to: null,
        label_ids: [],
        body_text: null,
        body_html: null,
        attachments: [],
      });
      const res = await getMail(row.id, activeId);
      setSelected(res.data.message);
    } catch (err: any) {
      setError(err.message || "Failed to open the mail");
      setSelected(null);
    } finally {
      setMessageLoading(false);
    }
  };

  const handleConnect = async (publicId?: string) => {
    try {
      setConnecting(true);
      setError(null);
      const res = await connectMailAccount(
        publicId
          ? { public_id: publicId }
          : {
              email: connectEmail.trim() || undefined,
              label: connectLabel.trim() || undefined,
            }
      );
      // Full-page navigation: Google will not render inside an iframe.
      window.location.href = res.data.url;
    } catch (err: any) {
      setError(err.message || "Could not start the Google connect");
      setConnecting(false);
    }
  };

  const handleDisconnect = async (publicId: string) => {
    try {
      await disconnectMailAccount(publicId);
      setNotice(
        "Tokens removed and cached mail kept. Revoke the grant at " +
          "myaccount.google.com/permissions to fully cut access."
      );
      await fetchAccounts();
    } catch (err: any) {
      setError(err.message || "Could not disconnect the mailbox");
    }
  };

  const handleSaveWindowDefault = async () => {
    if (!activeAccount || !windowFrom) return;
    const time = windowFrom.slice(11, 16);
    try {
      await updateMailAccount({
        public_id: activeAccount.public_id,
        fetch_after_time: time,
      });
      setNotice(`This mailbox now defaults to mail after ${time} each day.`);
      await fetchAccounts();
    } catch (err: any) {
      setError(err.message || "Could not save the default window");
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 p-8">
      {/* Heading. The connected address lives HERE so that hiding the mailbox
          card behind the gear never hides which inbox you're looking at. */}
      <div className="flex w-full flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">Mail box</span>
            {activeAccount && statusBadge(activeAccount)}
          </div>
          <span className="text-body font-body text-subtext-color">
            {activeAccount ? (
              <>
                <b>{activeAccount.email_address}</b> — browsing is served from our
                database; only "Fetch from Gmail" calls Google.
              </>
            ) : (
              <>
                Mail the Zoho Books bot reads. Browsing is served from our database —
                only "Fetch from Gmail" calls Google.
              </>
            )}
          </span>
        </div>
        <Button
          variant="neutral-tertiary"
          icon={<FeatherSettings />}
          onClick={() => setShowSettings((v) => !v)}
        >
          {showSettings ? "Hide settings" : "Settings"}
        </Button>
      </div>

      {(showSettings || !oauthConfigured) && (
        <MailSettingsCard
          defaultOpen={!oauthConfigured}
          onSaved={() => {
            setOauthConfigured(true);
            fetchAccounts();
          }}
        />
      )}

      {/* Messages */}
      {error && (
        <div className="flex w-full items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
          <FeatherAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-error-700" />
          <span className="text-body font-body text-error-700">{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex w-full items-start justify-between gap-2 rounded-md border border-solid border-success-200 bg-success-50 px-4 py-3">
          <span className="text-body font-body text-success-800">{notice}</span>
          <button
            className="text-caption font-caption text-success-800 underline"
            onClick={() => setNotice(null)}
          >
            dismiss
          </button>
        </div>
      )}

      {/* The mailbox — exactly one is live at a time.
          Hidden behind the Settings gear once a mailbox is connected and
          healthy (same treatment as the Google connection card). It forces
          itself visible when there is nothing connected, when the connection
          is in trouble, or mid Change-mailbox — hiding it in those states
          would lock the admin out of the only controls that can fix them. */}
      {(showSettings ||
        showConnect ||
        !activeAccount ||
        activeAccount.status !== "connected") && (
      <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
              <FeatherAtSign className="h-5 w-5 text-brand-600" />
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Mailbox
              </span>
              <span className="text-body font-body text-subtext-color">
                One mailbox at a time. Connecting a new one replaces it.
              </span>
            </div>
          </div>
        </div>

        {loadingAccounts ? (
          <span className="text-body font-body text-subtext-color">Loading mailbox…</span>
        ) : (
          <>
            {activeAccount && (
              <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-neutral-border bg-neutral-50 p-4">
                <div className="flex w-full flex-wrap items-center gap-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {activeAccount.label || activeAccount.email_address}
                  </span>
                  {statusBadge(activeAccount)}
                </div>
                <span className="text-caption font-caption text-subtext-color">
                  {activeAccount.email_address}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Window from {activeAccount.fetch_after_time.slice(0, 5)} ·{" "}
                  {activeAccount.fetch_timezone}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Last fetch{" "}
                  {activeAccount.last_synced_at
                    ? new Date(activeAccount.last_synced_at).toLocaleString()
                    : "never"}
                </span>
                {activeAccount.last_error && (
                  <span className="text-caption font-caption text-error-700">
                    {activeAccount.last_error}
                  </span>
                )}
              </div>
            )}

            {/*
              The connect form. Shown when there's no mailbox, or when the admin
              has asked to change it — never auto-filled with the CURRENT
              address, since the whole point of changing is to sign in as someone
              else and the backend refuses a mismatch between hint and signer.
            */}
            {showConnect || !activeAccount ? (
              <div className="flex w-full flex-col items-start gap-3">
                {activeAccount && (
                  <span className="text-body font-body text-subtext-color">
                    Connecting a new mailbox disconnects{" "}
                    <b>{activeAccount.email_address}</b>. Mail already fetched from
                    it stays in the database.
                  </span>
                )}
                <div className="flex w-full flex-wrap items-end gap-3">
                  <TextField
                    className="h-auto grow shrink-0 basis-0"
                    variant="outline"
                    label="Gmail address"
                    helpText="You must sign in as this exact account, or nothing is saved."
                  >
                    <TextField.Input
                      value={connectEmail}
                      onChange={(e) => setConnectEmail(e.target.value)}
                      placeholder="name@notionpress.com"
                    />
                  </TextField>
                  <TextField
                    className="h-auto grow shrink-0 basis-0"
                    variant="outline"
                    label="Label"
                  >
                    <TextField.Input
                      value={connectLabel}
                      onChange={(e) => setConnectLabel(e.target.value)}
                      placeholder="Zoho Books inbox"
                    />
                  </TextField>
                  <Button
                    icon={<FeatherMail />}
                    loading={connecting}
                    disabled={!oauthConfigured || !connectEmail.trim()}
                    onClick={() => handleConnect()}
                  >
                    {activeAccount ? "Connect & replace" : "Connect Gmail"}
                  </Button>
                  {activeAccount && (
                    <Button
                      variant="neutral-tertiary"
                      onClick={() => setShowConnect(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
                {!oauthConfigured && (
                  <span className="text-body font-body text-subtext-color">
                    Set up the Google connection above first.
                  </span>
                )}
              </div>
            ) : (
              <div className="flex w-full flex-wrap items-center gap-2">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  icon={<FeatherMail />}
                  onClick={() => {
                    // Blank, not prefilled — this flow exists to move to a
                    // DIFFERENT address.
                    setConnectEmail("");
                    setConnectLabel("");
                    setShowConnect(true);
                  }}
                >
                  Change mailbox
                </Button>
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  loading={connecting}
                  onClick={() => handleConnect(activeAccount.public_id)}
                  title="Re-run Google consent for this same address"
                >
                  Reconnect
                </Button>
                {activeAccount.has_refresh_token && (
                  <Button
                    variant="destructive-tertiary"
                    size="small"
                    onClick={() => handleDisconnect(activeAccount.public_id)}
                  >
                    Disconnect
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Window + list */}
      {activeAccount && (
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-neutral-border bg-default-background p-4">
            {/* No helpText on these fields: it would sit under one input and
                knock the two of them out of horizontal alignment. */}
            <div className="flex w-full flex-wrap items-end gap-3">
              <TextField className="h-auto" variant="outline" label="From">
                <TextField.Input
                  type="datetime-local"
                  value={windowFrom}
                  onChange={(e) => setWindowFrom(e.target.value)}
                />
              </TextField>
              <TextField className="h-auto" variant="outline" label="To">
                <TextField.Input
                  type="datetime-local"
                  value={windowTo}
                  onChange={(e) => setWindowTo(e.target.value)}
                />
              </TextField>
              <Button
                icon={<FeatherInbox />}
                loading={syncing}
                disabled={activeAccount.status === "disconnected"}
                onClick={handleSync}
              >
                Fetch from Gmail
              </Button>
              <Button
                variant="neutral-secondary"
                icon={<FeatherRefreshCw />}
                loading={loadingList}
                onClick={() => fetchList(activeAccount.public_id, windowFrom, windowTo)}
              >
                Reload
              </Button>
              <Button
                variant="neutral-tertiary"
                onClick={handleSaveWindowDefault}
                title="Remember this start time as this mailbox's daily default"
              >
                Save as default start
              </Button>
            </div>
            <span className="text-caption font-caption text-subtext-color">
              Times read in {windowTz || activeAccount.fetch_timezone}. Defaults to
              mail after {activeAccount.fetch_after_time.slice(0, 5)} — the most
              recent one, so after midnight it still means last night.
            </span>
          </div>

          {lastSync && (
            <span className="text-caption font-caption text-subtext-color">
              Gmail query: <code>{lastSync.query}</code> · {lastSync.duration_ms} ms
            </span>
          )}

          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 xl:flex-row">
            {/* List — hidden on narrow screens while a mail is open */}
            <div
              className={`w-full flex-col items-start overflow-hidden rounded-lg border border-solid border-neutral-border bg-default-background ${
                selected ? "hidden xl:flex xl:w-[420px] xl:shrink-0" : "flex"
              }`}
            >
              <div className="flex w-full items-center justify-between gap-2 border-b border-solid border-neutral-border px-4 py-3">
                <span className="text-body-bold font-body-bold text-default-font">
                  {total} mail{total === 1 ? "" : "s"} in window
                </span>
                {loadingList && (
                  <span className="text-caption font-caption text-subtext-color">
                    loading…
                  </span>
                )}
              </div>

              {messages.length === 0 ? (
                <div className="flex w-full flex-col items-start gap-1 px-4 py-6">
                  <span className="text-body font-body text-default-font">
                    Nothing cached for this window.
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    Hit "Fetch from Gmail" to pull it in.
                  </span>
                </div>
              ) : (
                <div className="w-full overflow-auto">
                  <Table
                    header={
                      <Table.HeaderRow>
                        <Table.HeaderCell>From</Table.HeaderCell>
                        <Table.HeaderCell>Subject</Table.HeaderCell>
                        <Table.HeaderCell>Sent to</Table.HeaderCell>
                        <Table.HeaderCell>Received</Table.HeaderCell>
                      </Table.HeaderRow>
                    }
                  >
                    {messages.map((row) => (
                      <Table.Row
                        key={row.id}
                        clickable
                        onClick={() => handleOpen(row)}
                        className={
                          selected?.id === row.id ? "bg-brand-50" : "cursor-pointer"
                        }
                      >
                        <Table.Cell className="max-w-[160px]">
                          <div className="flex flex-col items-start">
                            <span
                              className={`truncate text-body ${
                                row.is_unread
                                  ? "font-body-bold text-default-font"
                                  : "font-body text-default-font"
                              }`}
                              title={row.from_email || undefined}
                            >
                              {row.from_name || row.from_email || "Unknown"}
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="max-w-[280px]">
                          <div className="flex flex-col items-start gap-0.5">
                            <div className="flex w-full items-center gap-1.5">
                              {row.has_attachments && (
                                <FeatherPaperclip className="h-3 w-3 shrink-0 text-subtext-color" />
                              )}
                              <span
                                className={`truncate text-body ${
                                  row.is_unread
                                    ? "font-body-bold text-default-font"
                                    : "font-body text-default-font"
                                }`}
                              >
                                {row.subject || "(no subject)"}
                              </span>
                            </div>
                            <span className="line-clamp-1 text-caption font-caption text-subtext-color">
                              {row.snippet}
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          {/* Which address the sender actually used. A zoho-*
                              catch-all address is the per-client signal, so it
                              gets the brand colour; mail straight to the
                              mailbox stays muted. */}
                          {row.delivered_to &&
                          row.delivered_to.toLowerCase() !==
                            activeAccount.email_address.toLowerCase() ? (
                            <span className="whitespace-nowrap text-caption font-caption text-brand-700">
                              {row.delivered_to}
                            </span>
                          ) : (
                            <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
                              {row.delivered_to || "—"}
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
                            {formatWhen(row.received_at)}
                          </span>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table>
                </div>
              )}
            </div>

            {selected && (
              <MessageViewer
                message={selected}
                mailboxEmail={activeAccount.email_address}
                loading={messageLoading}
                onClose={() => setSelected(null)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BotMailboxPage;
